import { gql, TypedDocumentNode } from "@apollo/client";
import {
  PERSON_DETAILS_FRAGMENT,
  MEMBERSHIP_BAND_FRAGMENT,
  ADDRESS_FRAGMENT,
  PHONE_NUMBER_FRAGMENT,
  CCMS_SUSPENSION_FRAGMENT,
  CRM_NOTE_FIELDS_FRAGMENT
} from "@/fragments";
import { PAGE_INFO_FRAGMENT } from "@/hooks/usePagination";
import { PageInfo, PaginationVariables } from "@/components/Pagination";
import {
  CHANGE_REQUEST_CHANGE_FRAGMENT,
  CHANGE_REQUEST_FRAGMENT,
  Node,
  ChangeRequestStatus
} from "@/pages/sections/[sectionId]/change-requests/queries";
import { WHODUNNIT_FRAGMENT } from "@/pages/sections/[sectionId]/audit-log/queries";
import { PersonAuditEntry } from "@/pages/sections/types";
import {
  CommitteeAssignment,
  Employment,
  EnumOptions,
  Facility,
  FacilityCourseType,
  FacilityOperatingStatus,
  FacilityOperationType,
  FacilityType,
  Person,
  WorkingLocation
} from "@/types";

export type { Node };

// Re-export mutations from features for backward compatibility
export {
  UPDATE_ADDRESSES_MUTATION,
  UPDATE_PERSON_MUTATION,
  UPDATE_CONTACT_PREFERENCE_MUTATION,
  type AddressUpdate
} from "@/features/person/queries";

export type PersonOverviewQueryData = {
  person: Person & {
    committeeHistory?: CommitteeAssignment[];
    legacyCommitteeHistory?: CommitteeAssignment[];
  };
};

export type PersonOverviewQueryVariables = {
  pgaId: string;
  includeProgramMetrics: boolean;
  includeMembershipApplications: boolean;
  includeUniversityCohort: boolean;
  useCommitteeHistory: boolean;
};

export const PERSON_OVERVIEW_QUERY: TypedDocumentNode<PersonOverviewQueryData, PersonOverviewQueryVariables> = gql`
  query GetPersonOverview(
    $pgaId: ID!
    $includeProgramMetrics: Boolean!
    $includeMembershipApplications: Boolean!
    $includeUniversityCohort: Boolean!
    $useCommitteeHistory: Boolean!
  ) {
    person(pgaId: $pgaId) {
      ...PersonDetailsFragment
      age
      membershipSince
      totalDaysOfMembership
      activeDaysOfMembership
      duesBalance
      contactAddress {
        ...AddressFragment
      }
      latestProgram {
        id
        status
        programType {
          id
          name
        }
        band {
          id
          startedOn
          expiresOn
          createdReason
          terminatedReason
          terminatesOn
          classification {
            id
            name
          }
        }
      }
      ccmsSuspensions {
        ...CcmsSuspensionFragment
      }
      committeeHistory: committeeHistory @include(if: $useCommitteeHistory) {
        committeeName
        position
        startDate
        endDate
      }
      legacyCommitteeHistory: legacyCommitteeHistory @skip(if: $useCommitteeHistory) {
        committeeName
        position
        startDate
        endDate
      }
      accountCreatedAt @include(if: $includeProgramMetrics)
      totalJobApplications @include(if: $includeProgramMetrics)
      totalActiveJobApplications @include(if: $includeProgramMetrics)
      universityCohort @include(if: $includeUniversityCohort) {
        id
        name
      }
      membershipApplications @include(if: $includeMembershipApplications) {
        id
        applicationType
        status
      }
      bands @include(if: $includeProgramMetrics) {
        id
        createdReason
      }
      recruitmentLead @include(if: $includeProgramMetrics) {
        id
        referralSource
        entryPoint
        recruitmentDate
      }
      groupAssignments @include(if: $includeProgramMetrics) {
        id
        startedAt
        expiresAt
        group {
          id
          name
        }
      }
      prospectProgram {
        id
        membershipInterestForm {
          id
        }
        qualifyingLevel {
          status
          expirationDate: expiredAt
          endDate: completedAt
          completed @include(if: $includeProgramMetrics)
        }
        playingAbilityTestStatus {
          status
          startDate
          expirationDate
          expired
          activeSuspension {
            startDate
            endDate
          }
        }
        backgroundCheck {
          status
        }
        latestAssociateApplication @include(if: $includeProgramMetrics) {
          status
          startDate: startDt
          endDate: endDt
        }
        eligibleEmployment @include(if: $includeProgramMetrics) {
          id
        }
      }
      educationLatestCertification {
        startDate
        endDate
        programDescription
        careerPath
        subscription {
          expired
        }
      }
      educationLatestPgmProgram @include(if: $includeProgramMetrics) {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        pgmProgramExpirationDate
      }
      pgmProgressStatus @include(if: $includeProgramMetrics) {
        programType
        curriculumVersion
        currentLevelKey
        mostRecentCompletedLevelKey
        isGptpSuspended
        isSuspended
        levels {
          key
          name
          numeral
          status
          levelCode
          startDate
          completionDate
          expirationDate
          requirements {
            key
            name
            status
            completionDate
          }
        }
      }
      educationPgmProgramHistory {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        levelExpirationDate
        programRequirements {
          requireId
          notes
          remarks
          score
          levelCode
          completionStatus
          completionDate
          isCompleted
          isInProgress
          createDate
          requirementDefinition {
            levelCode
            courseDescription
            courseId
            description
            requireId
            requireType
          }
        }
      }
      careerInfo {
        hasCompensationProfile
        contactLastModifiedCompensationProfile
        hasJobPreferences
        jobPreferencesLastModified
        currentEmployerContainsGolfDirector
      }
      compensationProfiles {
        id
        updatedAt
        currentEmployerContainsGolfDirector
        isSnapshot
      }
      currentCompensationProfile {
        id
        contactLastModifiedDate
        currentEmployerContainsGolfDirector
      }
      jobPreferences {
        id
        updatedAt
      }
    }
  }
  ${PERSON_DETAILS_FRAGMENT}
  ${CCMS_SUSPENSION_FRAGMENT}
`;

export const PERSON_DETAILS_QUERY = gql`
  query GetPersonDetails($pgaId: ID!) {
    person(pgaId: $pgaId) {
      ...PersonDetailsFragment
      age
      dob {
        day
        month
        year
      }
      ssn {
        last4
        full
      }
      contactAddress {
        ...AddressFragment
      }
      latestProgram {
        id
        status
        programType {
          id
          name
        }
        band {
          id
          terminatedReason
          terminatesOn
          classification {
            id
            name
            description
          }
        }
      }
      primarySectionAffiliation {
        id
        startAt
      }
      totalJobApplications
      totalActiveJobApplications
      groupAssignments {
        id
        startedAt
        expiresAt
        group {
          id
          name
        }
      }
      university {
        id
        name
      }
      nameChangeDocument {
        id
        name
        category
        filename
        url
        createdAt
        updatedAt
      }
    }
  }
  ${PERSON_DETAILS_FRAGMENT}
  ${ADDRESS_FRAGMENT}
`;

export type GetInsightsResponse = {
  insights: {
    suggestedNote: string | null;
  };
};

export type GetInsightsVariables = {
  pgaId: string;
};

export const INSIGHTS_QUERY: TypedDocumentNode<GetInsightsResponse, GetInsightsVariables> = gql`
  query GetInsights($pgaId: ID!) {
    insights(pgaId: $pgaId) {
      suggestedNote
    }
  }
`;

export type QualifyingMembershipApplicationsQueryData = {
  person: {
    id: string;
    membershipApplications: Array<{
      id: string;
      applicationType: string;
      status: string;
      electionDate: { id: string; electionDate: string } | null;
    }>;
  };
};

export type QualifyingMembershipApplicationsQueryVariables = { pgaId: string };

/** Person membership applications used to deep-link Path to Membership requirement rows. Gated by manageApplications at the call site. */
export const GET_QUALIFYING_MEMBERSHIP_APPLICATIONS_QUERY: TypedDocumentNode<
  QualifyingMembershipApplicationsQueryData,
  QualifyingMembershipApplicationsQueryVariables
> = gql`
  query GetQualifyingMembershipApplications($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      membershipApplications {
        id
        applicationType
        status
        electionDate {
          id
          electionDate
        }
      }
    }
  }
`;

export const GET_MEMBERSHIP_DETAILS_QUERY = gql`
  query GetMembershipDetails($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      pgaId
      university {
        id
        name
      }
      lastAttendedPgmUniversity {
        id
        legacyId
        name
      }
      membershipSince
      totalDaysOfMembership
      activeDaysOfMembership
      duesBalance
      sectionByHomeAddress {
        id
        name
      }
      contactAddressId
      addresses {
        ...AddressFragment
      }
      employments {
        id
        isPrimary
        jobClassification
        intendedJobClassification
        title
        facility {
          id
          name
        }
      }
      bands {
        ...MembershipBandFragment
      }
      ccmsSuspensions {
        ...CcmsSuspensionFragment
        memo
        notes {
          ...CRMNoteFields
        }
      }
      latestProgram {
        id
        status
        programType {
          id
          name
        }
        band {
          id
          terminatesOn
          terminatedReason
          classification {
            id
            name
          }
        }
      }
      pgmProgressStatus {
        programType
        careerPath
        currentLevelCode
        currentLevelKey
        curriculumVersion
        isGptpSuspended
        isSuspended
        programExpirationDate
        mostRecentCompletedLevelKey
        patStatus
        levels {
          key
          name
          numeral
          status
          levelCode
          startDate
          completionDate
          expirationDate
          progressPercent
          requirements {
            key
            name
            status
            completionDate
            courseName
            seminarInfo {
              status
              eventId
              eventName
              eventLocation
              eventStartDate
              eventEndDate
              registrationDate
            }
          }
        }
        suspensions {
          id
          code
          startDate
          endDate
          note
          reasonDescription
        }
      }
      experienceCredits {
        total
        threshold
        complete
        employmentCredits
        educationCredits
        sources {
          origin
          sourceId
          creditType
          credits
          description
          startedOn
          endedOn
        }
        excludedSuspensionPeriods {
          code
          startedOn
          endedOn
        }
      }
      educationLatestPgmProgram {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        pgmProgramCompleted
        pgmProgramExpirationDate
      }
      educationPgmProgramHistory {
        curriculumVersion
        startDate
        endDate
        level
        levelCode
        careerPath
        programType
        completed
        pgmProgramExpirationDate
        levelExpirationDate
        programRequirements {
          requireId
          notes
          remarks
          score
          levelCode
          completionStatus
          completionDate
          isCompleted
          isInProgress
          createDate
          requirementDefinition {
            levelCode
            courseDescription
            courseId
            description
            requireId
            requireType
          }
        }
        testHistory {
          attemptNumber
          completionStatus
          isFailed
          isPassed
          isRetake
          levelCode
          maxScore
          score
          status
          testDate
          testId
          testDefinition {
            description
            levelCode
            maxScore
            oldTestId
            testId
          }
        }
      }
    }
  }
  ${MEMBERSHIP_BAND_FRAGMENT}
  ${ADDRESS_FRAGMENT}
  ${CCMS_SUSPENSION_FRAGMENT}
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export interface EmploymentRow extends Omit<Employment, "facility"> {
  facility: {
    id: string;
    name: string;
    section?: {
      id: string;
      name: string;
      legacyId?: string;
    };
  };
  workingLocation: WorkingLocation;
  affiliationSection?: {
    id: string;
    name: string;
  } | null;
  tourCode?: string;
  employmentKind: "MEMBER" | "NON_MEMBER";
}

export type NonMemberEmploymentJobTitle = {
  id: string;
  jobTitle: string;
};

type EmploymentDetailsPerson = {
  id: string;
  employments: EmploymentRow[];
  mainProgramType?: {
    id: string;
    name: string;
  } | null;
};

export const EMPLOYMENT_FRAGMENT = gql`
  fragment EmploymentFragment on BackofficeEmployment {
    endedOn
    facility {
      id
      name
      section {
        id
        name
        legacyId
      }
    }
    id
    isPrimary
    jobClassification
    intendedJobClassification
    sectionApprovalDate
    startedOn
    title
    formattedTitle
    workingLocation
    tourCode
    employmentKind
    notes {
      ...CRMNoteFields
    }
  }
  ${CRM_NOTE_FIELDS_FRAGMENT}
`;

export const GET_EMPLOYMENT_DETAILS_QUERY: TypedDocumentNode<{ person: EmploymentDetailsPerson }, { pgaId: string }> =
  gql`
    query GetEmploymentDetails($pgaId: ID!) {
      person(pgaId: $pgaId) {
        id
        mainProgramType {
          id
          name
        }
        employments {
          ...EmploymentFragment
        }
      }
    }
    ${EMPLOYMENT_FRAGMENT}
  `;

export const GET_SECTION_AFFILIATION_HISTORY_QUERY = gql`
  query GetSectionAffiliationHistory($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      primarySection {
        id
        name
      }
      sectionAffiliations {
        id
        primary
        startAt
        endAt
        section {
          id
          name
        }
      }
    }
  }
`;

export const GET_PERSON_CHANGE_REQUESTS_QUERY: TypedDocumentNode<
  { person: { changeRequests: { nodes: (Node | null)[]; pageInfo: PageInfo; totalCount: number } } },
  { pgaId: string; stage?: ChangeRequestStatus } & PaginationVariables
> = gql`
  query GetPersonChangeRequests(
    $after: String
    $before: String
    $first: Int
    $last: Int
    $pgaId: ID!
    $stage: BackofficeChangeRequestStatus
  ) {
    person(pgaId: $pgaId) {
      id
      changeRequests(stage: $stage, first: $first, after: $after, last: $last, before: $before) {
        nodes {
          ...ChangeRequest
          submittedAt
          ...Change
        }
        ...PageInfo
      }
    }
  }

  ${CHANGE_REQUEST_FRAGMENT}
  ${CHANGE_REQUEST_CHANGE_FRAGMENT}
  ${PAGE_INFO_FRAGMENT}
`;

export const GET_PROSPECT_PROGRAM_QUERY = gql`
  query GetProspectProgram($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      accountCreatedAt
      prospectProgram {
        id
        backgroundCheck {
          status
          startDate: screenedAt
          expirationDate: expiredAt
        }
        playingAbilityTestStatus {
          startDate
          expirationDate
          expired
          status
          completed
          submissions {
            id
            patType
            date
            expirationDate
            expired
            roundOneScore
            roundTwoScore
            roundOneId
            roundTwoId
            totalScore
            targetScore
            result
            additionalStatus
            note
            slug
            name
            tour
            handicap
            document {
              id
              url
              name
              category
            }
            suspension {
              startDate
              endDate
            }
          }
          activeSuspension {
            startDate
            endDate
          }
        }
        qualifyingLevel {
          status
          startDate
          expirationDate: expiredAt
          endDate: completedAt
          completed
        }
        membershipInterestForm {
          id
          startedAt
          reasonForBecomingMember
          howTheyHeard
        }
        latestAssociateApplication {
          status
          startDate: startDt
          endDate: endDt
        }
        eligibleEmployment {
          id
          startDate: startedOn
          endDate: endedOn
        }
      }
    }
  }
`;

export type CommitteeHistoryQueryData = {
  person: {
    id: string;
    committeeHistory?: CommitteeAssignment[];
    legacyCommitteeHistory?: CommitteeAssignment[];
  };
};

export type CommitteeHistoryQueryVariables = {
  pgaId: string;
  useCommitteeHistory: boolean;
};

export const GET_COMMITTEE_HISTORY_QUERY: TypedDocumentNode<CommitteeHistoryQueryData, CommitteeHistoryQueryVariables> =
  gql`
    query GetCommitteeHistory($pgaId: ID!, $useCommitteeHistory: Boolean!) {
      person(pgaId: $pgaId) {
        id
        committeeHistory: committeeHistory @include(if: $useCommitteeHistory) {
          committeeName
          position
          startDate
          endDate
          section {
            id
            name
            legacyId
          }
        }
        legacyCommitteeHistory: legacyCommitteeHistory @skip(if: $useCommitteeHistory) {
          committeeName
          position
          startDate
          endDate
        }
      }
    }
  `;

export type EmploymentFacilityQueryResponse = {
  employment: Employment;
  operatingStatusOptions: EnumOptions<FacilityOperatingStatus>;
  facilityTypeOptions: EnumOptions<FacilityType>;
  courseTypeOptions: EnumOptions<FacilityCourseType>;
  operationTypeOptions: EnumOptions<FacilityOperationType>;
};

export const GET_EMPLOYMENT_FACILITY_DETAILS_QUERY = gql`
  query GetEmploymentFacilityDetails($employmentId: ID!) {
    employment(id: $employmentId) {
      id
      facility {
        id
        name
        operatingStatus
        operationType
        courseType
        facilityType
        chapter {
          id
          code
          name
        }
        section {
          id
          name
        }
        phone {
          ...PhoneNumberFragment
        }
        fax {
          ...PhoneNumberFragment
        }
        golfShopPhone {
          ...PhoneNumberFragment
        }
        legacyId
        mainAddress {
          ...AddressFragment
        }
        mailingAddress {
          ...AddressFragment
        }
        parentCompany {
          id
          name
        }
      }
    }
    operatingStatusOptions: __type(name: "FacilityOperatingStatus") {
      enumValues {
        name
        description
      }
    }
    facilityTypeOptions: __type(name: "FacilityType") {
      enumValues {
        name
        description
      }
    }
    courseTypeOptions: __type(name: "FacilityCourseType") {
      enumValues {
        name
        description
      }
    }
    operationTypeOptions: __type(name: "FacilityOperationType") {
      enumValues {
        name
        description
      }
    }
  }
  ${ADDRESS_FRAGMENT}
  ${PHONE_NUMBER_FRAGMENT}
`;

export type JobTitle = {
  id: string;
  jobTitle: string;
  jobClassification: string;
};

export type TourCode = {
  id: string;
  code: string;
  name: string;
  current: boolean;
  facility: Pick<Facility, "id" | "name">;
};

type JobTitlesQueryResponse = {
  person: {
    id: string;
    availableJobTitles: JobTitle[];
  };
  tourCodes: TourCode[];
};

export const GET_JOB_TITLES_QUERY: TypedDocumentNode<JobTitlesQueryResponse, { personId: string }> = gql`
  query JobTitles($personId: ID!) {
    person(id: $personId) {
      id
      availableJobTitles {
        id
        jobTitle
        jobClassification
      }
    }
    tourCodes {
      id
      code
      name
      current
      facility {
        id
        name
      }
    }
  }
`;

export const GET_FACILITIES_SEARCH_QUERY = gql`
  query facilitiesSearchQuery($input: BackofficeCRMFacilitiesInput!) {
    facilities(input: $input) {
      nodes {
        id
        name
        mainAddress {
          ...AddressFragment
        }
      }
    }
  }
  ${ADDRESS_FRAGMENT}
`;

export type PersonAuditLogVariables = {
  pgaId: string;
  first?: number;
  last?: number;
  after?: string;
  before?: string;
};

type AuditLogResult = {
  person: {
    id: string;
    displayName: string;
    profilePhoto: string;
    auditLog: {
      totalCount: number;
      pageInfo: PageInfo;
      nodes: PersonAuditEntry[];
    };
  };
};

export const PERSON_AUDIT_LOG_QUERY: TypedDocumentNode<AuditLogResult, PersonAuditLogVariables> = gql`
  query personAuditLog($pgaId: ID!, $first: Int, $last: Int, $after: String, $before: String) {
    person(pgaId: $pgaId) {
      id
      displayName
      profilePhoto
      auditLog(first: $first, last: $last, after: $after, before: $before) {
        totalCount
        nodes {
          id
          changedAt
          description
          linkPath
          changeset {
            id
            typeOfData
            oldValue
            newValue
          }
          ...Whodunnit
        }
        ...PageInfo
      }
    }
  }

  ${PAGE_INFO_FRAGMENT}
  ${WHODUNNIT_FRAGMENT}
`;

// Query for person's dues orders
export type DuesOrdersData = {
  duesOrders: {
    nodes: Array<{
      id: string;
      stage: string;
      balance: number;
      dueDate?: string;
      payable: boolean;
      updatedAt: string;
      description: string;
      status: string;
      paymentStatus?: string;
      person: {
        id: string;
        pgaId: string;
      };
    }>;
  };
};

export const GET_PERSON_DUES_ORDERS_QUERY: TypedDocumentNode<
  DuesOrdersData,
  {
    pgaId: string;
    dueDateRange?: { from?: string; to?: string };
    statuses?: string[];
  }
> = gql`
  query GetPersonDuesOrders($pgaId: String!, $dueDateRange: DateRangeInput, $statuses: [CommerceOrderStatus!]) {
    duesOrders(first: 5, filters: { search: $pgaId, dueDateRange: $dueDateRange, statuses: $statuses }) {
      nodes {
        id
        stage
        balance
        dueDate
        payable
        updatedAt
        description
        status
        paymentStatus
        person {
          id
          pgaId
        }
      }
    }
  }
`;

export const GET_PERSON_GROUP_ASSIGNMENTS_QUERY = gql`
  query GetPersonGroupAssignments($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      groupAssignments {
        id
        startedAt
        expiresAt
        group {
          id
          name
        }
      }
      recruitmentLead {
        id
        referralSource
        entryPoint
        recruitmentDate
        zipcode
      }
    }
  }
`;

export const PERSON_BANDS_QUERY = gql`
  query PersonBands($pgaId: ID!) {
    person(pgaId: $pgaId) {
      id
      bands {
        ...MembershipBandFragment
      }
    }
  }
  ${MEMBERSHIP_BAND_FRAGMENT}
`;

export type BeneficiaryRecord = {
  id: string;
  fullName: string;
  beneficiaryType: "primary" | "contingent";
  relationship: string;
  percent: number;
  dateOfBirth: string;
  ssnLast4: string;
  applicationType: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
};

export type CcmsAssociateBeneficiaryRecord = {
  id: string;
  fullName: string | null;
  beneficiaryType: string | null;
  relationship: string | null;
  percent: number | null;
  dateOfBirth: string | null;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
};

export type CcmsMemberInsuranceRecord = {
  id: string;
  effectiveStartDate: string;
  effectiveEndDate: string | null;
  changedAt: string | null;
  author: string | null;
  benefLine1: string | null;
  benefLine1Name: string | null;
  benefLine1Relationship: string | null;
  benefLine2: string | null;
  benefLine2Name: string | null;
  benefLine2Relationship: string | null;
  benefLine3: string | null;
  benefLine3Name: string | null;
  benefLine3Relationship: string | null;
  benefLine4: string | null;
  benefLine4Name: string | null;
  benefLine4Relationship: string | null;
  benefLine5: string | null;
  benefLine5Name: string | null;
  benefLine5Relationship: string | null;
};

export type GetPersonBeneficiariesResponse = {
  person: {
    id: string;
    beneficiaries: BeneficiaryRecord[];
    latestProgram: {
      programType: {
        name: string;
      } | null;
    } | null;
    ccmsAssociateBeneficiaries: CcmsAssociateBeneficiaryRecord[];
    ccmsMemberInsuranceRecords: CcmsMemberInsuranceRecord[];
  };
};

export type GetPersonBeneficiariesVariables = {
  pgaId: string;
  includeInactive: boolean;
};

export const GET_PERSON_BENEFICIARIES_QUERY: TypedDocumentNode<
  GetPersonBeneficiariesResponse,
  GetPersonBeneficiariesVariables
> = gql`
  query GetPersonBeneficiaries($pgaId: ID!, $includeInactive: Boolean) {
    person(pgaId: $pgaId) {
      id
      beneficiaries(includeInactive: $includeInactive) {
        id
        fullName
        beneficiaryType
        relationship
        percent
        dateOfBirth
        ssnLast4
        applicationType
        effectiveStartDate
        effectiveEndDate
      }
      latestProgram {
        programType {
          name
        }
      }
      ccmsAssociateBeneficiaries {
        id
        fullName
        beneficiaryType
        relationship
        percent
        dateOfBirth
        effectiveStartDate
        effectiveEndDate
      }
      ccmsMemberInsuranceRecords {
        id
        effectiveStartDate
        effectiveEndDate
        changedAt
        author
        benefLine1
        benefLine1Name
        benefLine1Relationship
        benefLine2
        benefLine2Name
        benefLine2Relationship
        benefLine3
        benefLine3Name
        benefLine3Relationship
        benefLine4
        benefLine4Name
        benefLine4Relationship
        benefLine5
        benefLine5Name
        benefLine5Relationship
      }
    }
  }
`;

export const LEAD_REFERRAL_SOURCES_QUERY = gql`
  query ReferralSourcesOptions {
    referralSourceOptions: __type(name: "BackofficeRecruitmentReferralSourceEnum") {
      enumValues {
        name
        description
      }
    }
  }
`;
